import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  Brain,
  Target,
  Lightbulb,
  MessageCircle,
  Phone,
  Mail,
  Star,
  ArrowRight,
  Check,
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WA_NUMBER = "917006697421";
const WA_BASE = `https://wa.me/${WA_NUMBER}`;
const waLink = (msg) => `${WA_BASE}?text=${encodeURIComponent(msg)}`;

const WA_DISCOVERY = waLink(
  "Hi KKDucare, I'd like to book a free discovery call for my child"
);
const WA_PRO = waLink(
  "Hi KKDucare, I'm a working professional interested in your program"
);
const WA_REVIEWS = waLink(
  "Hi KKDucare, I'd like to learn more about your programs"
);
const WA_READY = waLink(
  "Hi KKDucare, I'm ready to start — please guide me"
);
const WA_PLAIN = WA_BASE;

/* ---------- Reveal-on-scroll hook ---------- */
const useReveal = () => {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".kk-reveal"));
    const vh = window.innerHeight || 800;

    // Reveal anything already in (or above) the viewport on mount
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh * 0.95) el.classList.add("kk-in");
    });

    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("kk-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("kk-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => {
      if (!el.classList.contains("kk-in")) io.observe(el);
    });
    // Safety net: ensure everything reveals after 1.2s (covers slow loads / SSR mismatch)
    const t = setTimeout(() => {
      els.forEach((el) => el.classList.add("kk-in"));
    }, 1200);
    return () => {
      io.disconnect();
      clearTimeout(t);
    };
  }, []);
};

/* ---------- Small primitives ---------- */
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
  >
    <path d="M19.11 4.91A10 10 0 0 0 4.07 18.2L3 22l3.91-1.03a10 10 0 0 0 12.2-15.06ZM12.04 20.2a8.18 8.18 0 0 1-4.17-1.14l-.3-.18-2.32.61.62-2.26-.2-.31a8.2 8.2 0 1 1 6.37 3.28Zm4.49-6.13c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.16.25-.64.8-.78.97-.15.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23a7.5 7.5 0 0 1-1.39-1.72c-.15-.25 0-.39.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.55-1.32-.75-1.81-.2-.49-.4-.42-.55-.43H7.4c-.16 0-.41.06-.62.31-.21.25-.82.8-.82 1.95s.84 2.27.96 2.43c.12.16 1.66 2.54 4.02 3.56.56.24 1 .39 1.34.5.56.18 1.07.15 1.47.09.45-.07 1.46-.6 1.66-1.18.21-.58.21-1.08.15-1.18-.06-.1-.22-.16-.47-.28Z" />
  </svg>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-20 md:py-32 ${className}`}>
    <div className="max-w-5xl mx-auto px-6 md:px-10">{children}</div>
  </section>
);

const SectionHeading = ({ overline, children, align = "center" }) => (
  <div
    className={`mb-12 md:mb-16 ${align === "center" ? "text-center" : ""}`}
  >
    {overline && (
      <p className="font-sans-body text-xs font-semibold tracking-[0.22em] uppercase text-[#B8922A] mb-4">
        {overline}
      </p>
    )}
    <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1B3A5B] font-semibold leading-tight">
      {children}
    </h2>
    {align === "center" && (
      <span className="kk-rule mx-auto mt-6" aria-hidden="true" />
    )}
  </div>
);

/* ---------- Navigation ---------- */
const Nav = () => (
  <header
    className="sticky top-0 z-40 bg-[#F2F0EB]/90 backdrop-blur-sm border-b border-[#B8922A]/25"
    data-testid="site-nav"
  >
    <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
      <a
        href="#top"
        className="inline-flex flex-col items-center"
        data-testid="nav-logo"
        aria-label="KKDucare home"
      >
        <span className="font-serif-display text-2xl md:text-[28px] text-[#1B3A5B] font-semibold tracking-tight leading-none">
          KKDucare
        </span>
        <span
          aria-hidden="true"
          className="mt-1.5 block h-px w-5 bg-[#B8922A]"
        />
      </a>
      <a
        href={WA_DISCOVERY}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white px-4 md:px-5 py-2.5 rounded-full text-sm md:text-[15px] font-medium transition-colors shadow-sm"
        data-testid="nav-whatsapp-btn"
      >
        <WhatsAppIcon className="w-4 h-4" />
        <span>WhatsApp Us</span>
      </a>
    </div>
  </header>
);

/* ---------- Hero ---------- */
const Hero = () => (
  <section
    id="top"
    className="relative pt-16 pb-20 md:pt-28 md:pb-32"
    data-testid="hero-section"
  >
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
      <p className="kk-reveal font-sans-body text-xs font-semibold tracking-[0.22em] uppercase text-[#B8922A] mb-6">
        Cognitive Development · K-12
      </p>

      <h1
        className="kk-reveal font-serif-display text-[40px] leading-[1.08] sm:text-5xl md:text-6xl lg:text-[68px] text-[#1B3A5B] font-semibold tracking-tight"
        data-testid="hero-headline"
      >
        Help your child think sharper, focus deeper,
        <span className="text-[#111111]"> and learn faster</span>
        <span className="text-[#B8922A]"> — for life.</span>
      </h1>

      <p className="kk-reveal mt-7 md:mt-9 font-sans-body text-base md:text-lg text-[#4A4A4A] leading-relaxed max-w-2xl mx-auto">
        Live, small-group cognitive development workshops led by{" "}
        <span className="text-[#111111] font-medium">
          Kiran Kumar Ganjoo
        </span>{" "}
        — 25+ years of teaching, 2,000+ students mentored across India.
      </p>

      <div className="kk-reveal mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
        <a
          href={WA_DISCOVERY}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center justify-center gap-2 bg-[#1B3A5B] hover:bg-[#0F2640] text-[#F2F0EB] font-medium text-base md:text-lg px-8 py-4 rounded-sm transition-colors w-full sm:w-auto min-h-[52px]"
          data-testid="hero-book-call-cta"
        >
          Book a Free Discovery Call
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </a>
        <a
          href={WA_DISCOVERY}
          target="_blank"
          rel="noopener noreferrer"
          className="font-sans-body text-[15px] text-[#1B3A5B] underline decoration-[#B8922A]/60 underline-offset-4 hover:decoration-[#B8922A] transition-colors"
          data-testid="hero-whatsapp-link"
        >
          Or WhatsApp us directly →
        </a>
      </div>

      <p className="kk-reveal mt-12 font-sans-body text-sm text-[#4A4A4A] flex items-center justify-center gap-2">
        <Star
          className="w-4 h-4 text-[#B8922A] fill-[#B8922A]"
          aria-hidden="true"
        />
        Trusted by 1,500+ students across 8+ Indian states
      </p>
    </div>
  </section>
);

/* ---------- Problem ---------- */
const ProblemCard = ({ quote, body, idx }) => (
  <div
    className="kk-reveal bg-white border border-[#B8922A]/30 p-8 md:p-9 flex flex-col"
    data-testid={`problem-card-${idx}`}
  >
    <p className="font-serif-display italic text-lg md:text-xl text-[#1B3A5B] leading-snug">
      “{quote}”
    </p>
    <span
      className="kk-rule mt-5 mb-5"
      aria-hidden="true"
    />
    <p className="font-sans-body text-[15px] md:text-base text-[#4A4A4A] leading-relaxed">
      {body}
    </p>
  </div>
);

const Problem = () => (
  <Section id="problem" className="bg-[#E8E4DC]">
    <SectionHeading overline="The Quiet Worry">Sound familiar?</SectionHeading>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
      <ProblemCard
        idx={1}
        quote="My child studies for hours but remembers very little."
        body="Memory isn't taught in schools — it's expected. Most students never learn how to retain what they study."
      />
      <ProblemCard
        idx={2}
        quote="Marks are okay, but I don't see real understanding or confidence."
        body="Scoring well and thinking well are two different things. One fades after exams. The other lasts a lifetime."
      />
      <ProblemCard
        idx={3}
        quote="I can see potential in my child, but no one is teaching them how to think."
        body="Schools teach what to learn. Almost no one teaches how to learn. That's the gap we exist to close."
      />
    </div>
  </Section>
);

/* ---------- What We Do ---------- */
const SkillCard = ({ icon: Icon, title, body, testid }) => (
  <div
    className="kk-reveal bg-[#F2F0EB] border border-[#B8922A]/30 p-8 md:p-10 flex flex-col"
    data-testid={testid}
  >
    <div className="w-11 h-11 rounded-sm border border-[#B8922A] flex items-center justify-center mb-6">
      <Icon className="w-5 h-5 text-[#B8922A]" strokeWidth={1.5} />
    </div>
    <h3 className="font-serif-display text-2xl text-[#1B3A5B] font-semibold mb-3">
      {title}
    </h3>
    <p className="font-sans-body text-[15px] md:text-base text-[#4A4A4A] leading-relaxed">
      {body}
    </p>
  </div>
);

const WhatWeDo = () => (
  <Section id="what-we-do">
    <div className="text-center mb-12 md:mb-16 kk-reveal">
      <p className="font-sans-body text-xs font-semibold tracking-[0.22em] uppercase text-[#B8922A] mb-4">
        Our Approach
      </p>
      <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1B3A5B] font-semibold leading-tight max-w-3xl mx-auto">
        We don't teach subjects.{" "}
        <span className="text-[#111111]">
          We teach the mind behind learning.
        </span>
      </h2>
      <span className="kk-rule mx-auto mt-6" aria-hidden="true" />
      <p className="mt-6 font-sans-body text-base md:text-lg text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
        Four foundational skills, taught live, in small cohorts — week after
        week, until they become second nature.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
      <SkillCard
        testid="skill-memory"
        icon={Brain}
        title="Memory"
        body="Proven techniques to retain more in less time — without rote repetition."
      />
      <SkillCard
        testid="skill-focus"
        icon={Target}
        title="Focus"
        body="Sustained attention in a world built to distract."
      />
      <SkillCard
        testid="skill-thinking"
        icon={Lightbulb}
        title="Thinking"
        body="Logical reasoning, problem-solving, and creative connection."
      />
      <SkillCard
        testid="skill-confidence"
        icon={MessageCircle}
        title="Confidence"
        body="The ability to speak, ask, and lead — in any room."
      />
    </div>

    <p className="kk-reveal mt-12 md:mt-16 text-center font-sans-body italic text-sm md:text-[15px] text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
      Also serving working professionals (ages 25–45) building sharper memory,
      focus, and decision-making.{" "}
      <a
        href={WA_PRO}
        target="_blank"
        rel="noopener noreferrer"
        className="not-italic text-[#1B3A5B] underline decoration-[#B8922A]/60 underline-offset-4 hover:decoration-[#B8922A]"
        data-testid="professionals-link"
      >
        Get in touch →
      </a>
    </p>
  </Section>
);

/* ---------- Method (Weave & Witness) ---------- */
const Method = () => (
  <Section id="method">
    <div className="max-w-3xl mx-auto">
      <div className="kk-reveal border-l-2 border-[#1B3A5B] pl-7 md:pl-10">
        <p className="font-sans-body text-xs font-semibold tracking-[0.22em] uppercase text-[#B8922A] mb-4">
          Our Method
        </p>
        <h2
          className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1B3A5B] font-semibold leading-tight"
          data-testid="method-heading"
        >
          The Weave &amp; Witness Method
        </h2>
        <span className="kk-rule mt-6" aria-hidden="true" />

        <div className="mt-8 font-sans-body text-base md:text-lg text-[#111111] leading-relaxed space-y-6">
          <p>
            Most teaching methods focus on what to learn. The Weave &amp;
            Witness Method focuses on how the mind learns — and builds that
            capacity deliberately, over time.
          </p>
          <p>
            Developed over 25 years by Kiran Kumar Ganjoo and filed with the
            Government of India&apos;s Copyright Office, the Weave &amp; Witness
            Method is a structured framework for cognitive development —
            combining active engagement techniques with reflective practice to
            strengthen memory, focus, reasoning, and confidence.
          </p>
          <p className="text-[#1B3A5B] font-medium">
            It is the foundation of every KKDucare session.
          </p>
        </div>

        <p
          className="mt-10 font-sans-body text-xs tracking-[0.12em] uppercase text-[#B8922A]"
          data-testid="method-credential"
        >
          © Weave &amp; Witness Method — Registered with Government of India
          Copyright Office
        </p>
      </div>
    </div>
  </Section>
);

/* ---------- Testimonials ---------- */
const TestimonialCard = ({ quote, name, relation, location, testid }) => (
  <div
    className="kk-reveal bg-white border border-[#B8922A]/30 p-8 md:p-9 flex flex-col"
    data-testid={testid}
  >
    <div className="flex gap-1 mb-5" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className="w-4 h-4 text-[#B8922A] fill-[#B8922A]"
        />
      ))}
    </div>
    <p className="font-serif-display italic text-lg text-[#111111] leading-snug">
      “{quote}”
    </p>
    <span className="kk-rule mt-6 mb-5" aria-hidden="true" />
    <div className="font-sans-body">
      <p className="text-[#1B3A5B] font-semibold text-base">— {name}</p>
      <p className="italic text-sm text-[#4A4A4A] mt-1">{relation}</p>
      <p className="italic text-sm text-[#4A4A4A]">{location}</p>
    </div>
  </div>
);

const Testimonials = () => (
  <Section id="testimonials" className="bg-[#E8E4DC]">
    <SectionHeading overline="Student Voices">
      What students are saying
    </SectionHeading>
    <p className="kk-reveal -mt-6 mb-12 md:mb-16 text-center font-sans-body text-base md:text-lg text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
      Real words from real students — across cities, grades, and journeys.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
      <TestimonialCard
        testid="testimonial-1"
        quote="I joined the English workshop at KKDucare when I was in Class 7. At that time, I was struggling academically and heavily relied on rote learning. Over the past few years, this training has completely transformed my approach to studies. Today, I contribute confidently in class without depending on memorisation. KKDucare has shaped me into a self-reliant learner and prepared me to be a valuable resource in any global setting."
        name="Mishika Raina"
        relation="Student, Class 7 (joined)"
        location="Jammu"
      />
      <TestimonialCard
        testid="testimonial-2"
        quote="Before joining KKDucare, I found it hard to express my thoughts in English. Through group discussions, creative writing, and interactive sessions, I've developed strong communication skills and a deeper understanding of the language. Now, I feel confident speaking in front of an audience."
        name="Ayush Badyal"
        relation="Student, Class 10"
        location="Jammu"
      />
      <TestimonialCard
        testid="testimonial-3"
        quote="I've been a part of KKDucare for over 2 years now, and it has truly transformed me. Through presentations, debates, and role plays — without even using notebooks or pens — I've been able to rewire my thinking, improve my decision-making skills, and develop my personality. The unique approach of learning through doing has helped me grow both personally and professionally."
        name="Sakshi Sawarkat"
        relation="Student, 2+ years at KKDucare"
        location="Pune"
      />
      <TestimonialCard
        testid="testimonial-4"
        quote="I am deeply grateful for the efforts and dedication devoted towards helping me grow and become a resource for this country. I could never find a better guide — and I will always remain a student who aims to make him proud one day."
        name="Suhas Raina"
        relation="Student, KKDucare"
        location="Jammu"
      />
    </div>

    <p className="kk-reveal mt-12 text-center">
      <a
        href={waLink(
          "Hi KKDucare, I'd like to watch testimonial videos"
        )}
        target="_blank"
        rel="noopener noreferrer"
        className="font-sans-body italic text-[14px] text-[#1B3A5B] hover:text-[#0F2640] underline decoration-[#B8922A]/60 underline-offset-4 hover:decoration-[#B8922A]"
        data-testid="watch-stories-link"
      >
        ▶ Prefer to hear directly from students and parents? Watch their stories →
      </a>
    </p>
  </Section>
);

/* ---------- About ---------- */
const FounderPhoto = () => (
  <div className="flex md:justify-start justify-center" data-testid="founder-photo">
    <img
      src="https://customer-assets.emergentagent.com/job_learning-sharp/artifacts/2p9a5zt4_Screenshot%202026-05-06%20at%2012.00.53%E2%80%AFAM.png"
      alt="Kiran Kumar Ganjoo, Founder & Lead Educator at KKDucare"
      width="280"
      height="280"
      loading="lazy"
      decoding="async"
      className="w-[280px] h-[280px] rounded-full object-cover ring-[3px] ring-[#1B3A5B] ring-offset-[4px] ring-offset-[#F2F0EB]"
    />
  </div>
);

const About = () => (
  <Section id="about">
    <SectionHeading overline="About">
      Meet the educator behind KKDucare
    </SectionHeading>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
      <div className="md:col-span-5 kk-reveal">
        <FounderPhoto />
      </div>

      <div className="md:col-span-7 kk-reveal">
        <h3 className="font-serif-display text-3xl md:text-4xl text-[#1B3A5B] font-semibold">
          Kiran Kumar Ganjoo
        </h3>
        <p className="font-sans-body italic text-[#B8922A] mt-1">
          Founder &amp; Lead Educator
        </p>
        <span className="kk-rule mt-5 mb-7" aria-hidden="true" />

        <div className="font-sans-body text-[15px] md:text-base text-[#111111] leading-relaxed space-y-5">
          <p>
            For over 25 years, Kiran Kumar Ganjoo has helped students across
            India build the cognitive foundations that traditional schools miss
            — memory, focus, reasoning, and confidence.
          </p>
          <p>
            What started as classroom teaching is now a fully online practice,
            reaching families in 8+ states. Through KKDucare, he continues the
            work he began decades ago: teaching students how to think, not just
            what to memorize.
          </p>
          <p>
            Over 2,000 students have been personally mentored under his
            methodology — from Kashmir to Karnataka, from Class 3 to college.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
          <div>
            <p className="font-serif-display text-3xl text-[#1B3A5B] font-semibold">
              25+
            </p>
            <p className="font-sans-body text-xs text-[#4A4A4A] mt-1">
              Years teaching
            </p>
          </div>
          <div>
            <p className="font-serif-display text-3xl text-[#1B3A5B] font-semibold">
              2,000+
            </p>
            <p className="font-sans-body text-xs text-[#4A4A4A] mt-1">
              Students mentored
            </p>
          </div>
          <div>
            <p className="font-serif-display text-3xl text-[#1B3A5B] font-semibold">
              8+
            </p>
            <p className="font-sans-body text-xs text-[#4A4A4A] mt-1">
              Indian states
            </p>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

/* ---------- How It Works ---------- */
const Step = ({ n, title, body, testid }) => (
  <div
    className="kk-reveal relative pl-16 md:pl-0 md:text-center"
    data-testid={testid}
  >
    <div className="md:mx-auto w-12 h-12 md:w-14 md:h-14 rounded-full border border-[#B8922A] bg-[#F2F0EB] flex items-center justify-center font-serif-display text-xl md:text-2xl text-[#1B3A5B] font-semibold absolute md:relative top-0 left-0">
      {n}
    </div>
    <h3 className="font-serif-display text-xl md:text-[22px] text-[#1B3A5B] font-semibold mt-0 md:mt-6">
      {title}
    </h3>
    <p className="font-sans-body text-[15px] text-[#4A4A4A] mt-2 leading-relaxed md:px-2">
      {body}
    </p>
  </div>
);

const HowItWorks = () => (
  <Section id="how">
    <SectionHeading overline="The Path">
      Simple to start. Easy to continue.
    </SectionHeading>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 relative">
      {/* connecting line on desktop */}
      <span
        aria-hidden="true"
        className="hidden md:block absolute top-7 left-[12.5%] right-[12.5%] h-px bg-[#B8922A]/40"
      />
      <Step
        n="1"
        testid="step-1"
        title="Book a free discovery call"
        body="A 30-minute conversation to understand your child's needs."
      />
      <Step
        n="2"
        testid="step-2"
        title="Meet on Google Meet"
        body="We assess where your child is today — strengths, gaps, and goals."
      />
      <Step
        n="3"
        testid="step-3"
        title="Choose the right program"
        body="We recommend the program that fits — together."
      />
      <Step
        n="4"
        testid="step-4"
        title="Start the journey"
        body="First class within 7 days. Live, small-group, taught by experienced educators."
      />
    </div>

    <p className="kk-reveal mt-14 text-center font-sans-body italic text-[#4A4A4A]">
      Free. No commitment. Just a real conversation.
    </p>
  </Section>
);

/* ---------- Final CTA + Lead Form ---------- */
const FinalCTA = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    child_grade: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        child_grade: form.child_grade.trim() || null,
        source: "landing-final-cta",
      });
      setSubmitted(true);
      toast.success("Thank you. We'll reach out within 24 hours.");
      setForm({ name: "", phone: "", child_grade: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section id="cta" className="bg-[#1B3A5B] text-[#F2F0EB]">
      <div className="max-w-3xl mx-auto text-center">
        <p className="kk-reveal font-sans-body text-xs font-semibold tracking-[0.22em] uppercase text-[#B8922A] mb-5">
          Begin
        </p>
        <h2 className="kk-reveal font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
          Your child's mind is their most important asset.
          <span className="block text-[#B8922A] mt-2">
            Let's build it together.
          </span>
        </h2>
        <span className="kk-rule mx-auto mt-7" aria-hidden="true" />
      </div>

      <div className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-start max-w-4xl mx-auto">
        {/* WhatsApp CTA */}
        <div className="kk-reveal text-center md:text-left">
          <h3 className="font-serif-display text-2xl text-[#F2F0EB] font-semibold">
            Speak with us on WhatsApp
          </h3>
          <p className="font-sans-body text-sm text-[#F2F0EB]/75 mt-3 leading-relaxed">
            The fastest way to reach our team. We typically reply within an
            hour during working days.
          </p>

          <a
            href={WA_READY}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center justify-center gap-2 bg-[#B8922A] hover:bg-[#B5965B] text-[#1B3A5B] font-semibold px-8 py-4 rounded-sm w-full md:w-auto min-h-[52px] transition-colors"
            data-testid="final-cta-button"
          >
            Book Your Free Discovery Call
            <ArrowRight className="w-4 h-4" />
          </a>

          <p className="mt-4 font-sans-body text-xs text-[#F2F0EB]/70">
            Free • No commitment • 30 minutes • Speak directly with our team
          </p>
          <p className="mt-3 font-sans-body italic text-sm">
            Prefer WhatsApp?{" "}
            <a
              href={WA_REVIEWS}
              target="_blank"
              rel="noopener noreferrer"
              className="not-italic text-[#B8922A] underline decoration-[#B8922A]/60 underline-offset-4 hover:decoration-[#B8922A]"
              data-testid="final-whatsapp-text-link"
            >
              Message us here →
            </a>
          </p>
        </div>

        {/* Lead form */}
        <div className="kk-reveal bg-[#F2F0EB] text-[#111111] p-7 md:p-9 border border-[#B8922A]/40">
          {submitted ? (
            <div className="text-center py-6" data-testid="lead-form-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-[#1B3A5B] flex items-center justify-center mb-5">
                <Check className="w-6 h-6 text-[#B8922A]" />
              </div>
              <h3 className="font-serif-display text-2xl text-[#1B3A5B] font-semibold">
                Thank you.
              </h3>
              <p className="font-sans-body text-sm text-[#4A4A4A] mt-3 leading-relaxed">
                Our team will reach out within 24 hours to schedule your free
                discovery call.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="mt-5 font-sans-body text-sm text-[#1B3A5B] underline decoration-[#B8922A]/60 underline-offset-4"
                data-testid="lead-form-reset"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="flex flex-col gap-5"
              data-testid="lead-form"
              noValidate
            >
              <div>
                <h3 className="font-serif-display text-2xl text-[#1B3A5B] font-semibold">
                  Request a callback
                </h3>
                <p className="font-sans-body text-sm text-[#4A4A4A] mt-2">
                  Leave your details. We'll call you back.
                </p>
              </div>

              <label className="flex flex-col gap-2">
                <span className="font-sans-body text-xs font-semibold tracking-wider uppercase text-[#1B3A5B]">
                  Parent's name
                </span>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  autoComplete="name"
                  placeholder="Your full name"
                  className="bg-transparent border-b border-[#1B3A5B]/25 focus:border-[#1B3A5B] py-2.5 outline-none font-sans-body text-base placeholder:text-[#4A4A4A]/60"
                  data-testid="lead-form-name"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="font-sans-body text-xs font-semibold tracking-wider uppercase text-[#1B3A5B]">
                  Phone number
                </span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  required
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+91 XXXXX XXXXX"
                  className="bg-transparent border-b border-[#1B3A5B]/25 focus:border-[#1B3A5B] py-2.5 outline-none font-sans-body text-base placeholder:text-[#4A4A4A]/60"
                  data-testid="lead-form-phone"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="font-sans-body text-xs font-semibold tracking-wider uppercase text-[#1B3A5B]">
                  Child's grade <span className="font-normal lowercase tracking-normal text-[#4A4A4A]">(optional)</span>
                </span>
                <input
                  type="text"
                  name="child_grade"
                  value={form.child_grade}
                  onChange={onChange}
                  placeholder="e.g. Grade 7"
                  className="bg-transparent border-b border-[#1B3A5B]/25 focus:border-[#1B3A5B] py-2.5 outline-none font-sans-body text-base placeholder:text-[#4A4A4A]/60"
                  data-testid="lead-form-grade"
                />
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#1B3A5B] hover:bg-[#0F2640] disabled:opacity-60 text-[#F2F0EB] font-medium px-6 py-3.5 rounded-sm transition-colors min-h-[48px]"
                data-testid="lead-form-submit"
              >
                {submitting ? "Sending…" : "Request Callback"}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>

              <p className="font-sans-body text-xs text-[#4A4A4A]">
                By submitting, you agree to be contacted about KKDucare programs.
              </p>
            </form>
          )}
        </div>
      </div>
    </Section>
  );
};

/* ---------- Footer ---------- */
const Footer = () => (
  <footer className="bg-[#F2F0EB] border-t border-[#B8922A]/30" data-testid="site-footer">
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-14 md:py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-8 items-start">
        <div>
          <p className="font-serif-display text-2xl md:text-[28px] text-[#1B3A5B] font-semibold leading-none">
            KKDucare
          </p>
          <span
            aria-hidden="true"
            className="mt-2 block h-px w-5 bg-[#B8922A]"
          />
          <p className="font-sans-body text-sm text-[#4A4A4A] mt-5 leading-relaxed max-w-md">
            Live cognitive development workshops for Indian students.
          </p>
          <p
            className="font-sans-body text-xs text-[#4A4A4A]/70 mt-4 leading-relaxed max-w-md"
            data-testid="footer-trademark-line"
          >
            KKducare™ — Trademark application filed | Weave &amp; Witness
            Method © Government of India Copyright Office
          </p>
        </div>

        <div className="md:text-right">
          <p className="font-sans-body text-xs font-semibold tracking-[0.22em] uppercase text-[#B8922A] mb-4">
            Contact
          </p>
          <ul className="font-sans-body text-sm text-[#111111] space-y-3">
            <li className="flex items-center gap-2 md:justify-end">
              <Phone className="w-4 h-4 text-[#1B3A5B]" strokeWidth={1.5} />
              <a
                href={WA_PLAIN}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#1B3A5B]"
                data-testid="footer-whatsapp-link"
              >
                +91 70066 97421
              </a>
            </li>
            <li className="flex items-center gap-2 md:justify-end">
              <Mail className="w-4 h-4 text-[#1B3A5B]" strokeWidth={1.5} />
              <a
                href="mailto:kiranganjoo13@gmail.com"
                className="hover:text-[#1B3A5B]"
                data-testid="footer-email-link"
              >
                kiranganjoo13@gmail.com
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-[#B8922A]/20 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <p className="font-sans-body text-xs text-[#4A4A4A]">
          © 2026 KKDucare. All rights reserved.
        </p>
        <p className="font-sans-body text-xs text-[#4A4A4A]">
          Built with care for Indian families.
        </p>
      </div>
    </div>
  </footer>
);

/* ---------- Sticky mobile WhatsApp ---------- */
const StickyWhatsApp = () => (
  <a
    href={WA_PLAIN}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Chat with KKDucare on WhatsApp"
    className="md:hidden fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:bg-[#1FB855] transition-colors"
    data-testid="sticky-whatsapp-btn"
  >
    <WhatsAppIcon className="w-7 h-7" />
  </a>
);

/* ---------- App ---------- */
function App() {
  useReveal();

  // Sanity ping (optional, non-blocking)
  const pingedRef = useRef(false);
  useEffect(() => {
    if (pingedRef.current) return;
    pingedRef.current = true;
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App bg-[#F2F0EB] text-[#111111]">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            fontFamily: "Inter, sans-serif",
            border: "1px solid rgba(200,168,90,0.4)",
          },
        }}
      />
      <Nav />
      <main>
        <Hero />
        <Problem />
        <WhatWeDo />
        <Method />
        <Testimonials />
        <About />
        <HowItWorks />
        <FinalCTA />
      </main>
      <Footer />
      <StickyWhatsApp />
    </div>
  );
}

export default App;
